import React, { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiPlus, FiX } from "react-icons/fi";
import SEO from "../components/SEO";
import useReveal from "../hooks/useReveal";

// TS EN 13791 core-length-factor: l/d=1 -> 0.82, l/d=2 -> 1.00 (linear between, clamped)
const clf = (ld) => {
  if (ld >= 2) return 1.0;
  if (ld <= 1) return 0.82;
  return 0.82 + (ld - 1) * 0.18;
};

function evaluate(cores, ld, kFactor) {
  const vals = cores.map((c) => parseFloat(String(c).replace(",", "."))).filter((v) => !isNaN(v) && v > 0);
  const n = vals.length;
  if (n < 3) return { ok: false };
  const factor = clf(ld > 0 ? ld : 2);
  const corr = vals.map((v) => v * factor);
  const fm = corr.reduce((a, b) => a + b, 0) / n;
  const fmin = Math.min(...corr);
  const variance = corr.reduce((a, b) => a + (b - fm) ** 2, 0) / (n - 1);
  const s = Math.sqrt(variance);

  let approach, cand1, cand2, k;
  if (n >= 15) {
    approach = "A";
    const sEff = Math.max(s, 2.0);
    cand1 = fm - 1.48 * sEff;
    cand2 = fmin + 4;
    k = 1.48;
  } else {
    approach = "B";
    k = n >= 10 ? 5 : n >= 7 ? 6 : 7;
    cand1 = fm - k;
    cand2 = fmin + 4;
  }
  const fck = Math.min(cand1, cand2);
  const governing = cand1 <= cand2 ? "mean" : "lowest";
  return {
    ok: true, n, factor, fm, fmin, s, approach, k, cand1, cand2,
    fck: Math.max(0, fck), design: Math.max(0, fck) * kFactor, governing,
  };
}

const f1 = (x) => (isFinite(x) ? x.toFixed(1) : "–");

export default function BetonKarot() {
  const { t } = useTranslation();
  const pageRef = useRef(null);
  useReveal(pageRef, [t]);
  const T = (k) => t(`tools.betonKarot.${k}`);

  const [cores, setCores] = useState(["", "", ""]);
  const [ld, setLd] = useState("2.0");
  const [kFactor, setKFactor] = useState(1.0);

  const res = useMemo(() => evaluate(cores, parseFloat(ld.replace(",", ".")) || 2, kFactor), [cores, ld, kFactor]);

  const setCore = (i, v) => setCores((c) => c.map((x, j) => (j === i ? v : x)));
  const addCore = () => setCores((c) => [...c, ""]);
  const removeCore = (i) => setCores((c) => (c.length > 1 ? c.filter((_, j) => j !== i) : c));
  const reset = () => { setCores(["", "", ""]); setLd("2.0"); setKFactor(1.0); };

  return (
    <div ref={pageRef}>
      <SEO title={T("meta_title")} description={T("meta_desc")} />

      <section className="page-hero blueprint" style={{ borderBottom: 0 }}>
        <div className="container ph-inner">
          <Link to="/yazilimlar#araclar" className="article-back link-arrow"><FiArrowLeft /> {T("back")}</Link>
          <span className="eyebrow reveal">{T("eyebrow")}</span>
          <h1 className="reveal d1" style={{ fontSize: "clamp(34px,5vw,64px)" }}>{T("title")}</h1>
          <p className="ph-lede reveal d2">{T("lede")}</p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 24 }}>
        <div className="container">
          <div className="calc-grid">
            {/* INPUTS */}
            <div className="calc-panel reveal">
              <div className="field">
                <label>{T("ldRatio")}</label>
                <input type="number" step="0.05" min="1" max="2" value={ld} onChange={(e) => setLd(e.target.value)} />
                <span className="calc-hint">{T("ldNote")}</span>
              </div>

              <div className="field">
                <label>{T("coresLabel")} · {T("unit")}</label>
                <span className="calc-hint" style={{ marginBottom: 6 }}>{T("coresHint")}</span>
                <div className="core-list">
                  {cores.map((c, i) => (
                    <div className="core-row" key={i}>
                      <span className="core-idx">{i + 1}</span>
                      <input type="number" step="0.1" min="0" value={c} placeholder="0.0"
                        onChange={(e) => setCore(i, e.target.value)} />
                      <button className="core-del" onClick={() => removeCore(i)} aria-label="remove"><FiX /></button>
                    </div>
                  ))}
                </div>
                <button className="btn ghost sm" style={{ marginTop: 12 }} onClick={addCore}><FiPlus /> {T("addCore")}</button>
              </div>

              <div className="field">
                <label>{T("knowledge")}</label>
                <div className="chip-row">
                  <span className={`chip${kFactor === 0.75 ? " on" : ""}`} onClick={() => setKFactor(0.75)}>{T("knowledgeLimited")}</span>
                  <span className={`chip${kFactor === 1.0 ? " on" : ""}`} onClick={() => setKFactor(1.0)}>{T("knowledgeComprehensive")}</span>
                </div>
              </div>

              <button className="btn ghost sm" onClick={reset} style={{ marginTop: 6 }}>{T("reset")}</button>
            </div>

            {/* RESULTS */}
            <div className="calc-panel calc-results reveal d1">
              <div className="calc-res-head">{T("results")}</div>
              {res.ok ? (
                <>
                  <div className="calc-kv"><span>{T("resCount")}</span><b>{res.n}</b></div>
                  <div className="calc-kv"><span>{T("resCorrected")}</span><b>{f1(res.fm)} {T("unit")}</b></div>
                  <div className="calc-kv"><span>{T("resMin")}</span><b>{f1(res.fmin)} {T("unit")}</b></div>
                  <div className="calc-kv"><span>{T("resStd")}</span><b>{f1(res.s)} {T("unit")}</b></div>
                  <div className="calc-kv"><span>{T("resApproach")}</span><b>{res.approach} · k={res.k}</b></div>
                  <div className="calc-hl">
                    <span>{T("resFck")}</span>
                    <strong>{f1(res.fck)} <em>{T("unit")}</em></strong>
                  </div>
                  <div className="calc-kv"><span>{T("resDesign")} ({kFactor.toFixed(2)})</span><b className="accent">{f1(res.design)} {T("unit")}</b></div>
                  <div className="calc-kv sub"><span>{T("governing")}</span>
                    <b>{res.governing === "mean" ? "f_m − k·s" : "f_is,min + 4"}</b></div>
                </>
              ) : (
                <div className="calc-empty">{T("needMore")}</div>
              )}
            </div>
          </div>

          {/* METHOD */}
          <div className="calc-method reveal">
            <div className="cm-title">{T("formulaTitle")}</div>
            <ul>
              <li>{T("ldFormula")}</li>
              <li>{T("formulaA")}</li>
              <li>{T("formulaB")}</li>
            </ul>
            <p className="calc-note">{T("note")}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
